import React, { useState, useCallback, useEffect } from 'react';
import { string } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';
import { Heading, Text, Box } from '@landtrustinc/design-system';
import { apiBaseUrl } from '../../util/api';
import { ReactComponent as IconCalendar } from '../../assets/icons/calendar.svg';

import css from './CalendarExport.module.css';

const CalendarExport = props => {
  const { className, rootClassName, listingId } = props;
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState(null);
  const [hasExistingSubscription, setHasExistingSubscription] = useState(false);
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const classes = classNames(rootClassName || css.root, className);

  // Check for existing subscription on mount
  useEffect(() => {
    const checkExistingSubscription = async () => {
      if (!listingId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${apiBaseUrl()}/api/calendar/subscription`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            listingId: listingId.uuid,
            regenerate: false,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.subscriptionUrl && !data.isNew) {
            setSubscriptionUrl(data.subscriptionUrl);
            setHasExistingSubscription(true);
          }
        }
      } catch (err) {
        // Silently fail - user can still create a new subscription
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingSubscription();
  }, [listingId]);

  const handleCreateSubscription = useCallback(
    async (regenerate = false) => {
      if (!listingId || isCreatingSubscription) return;

      setIsCreatingSubscription(true);
      setError(null);
      setShowRegenerateConfirm(false);

      try {
        const response = await fetch(`${apiBaseUrl()}/api/calendar/subscription`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            listingId: listingId.uuid,
            regenerate,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create subscription');
        }

        const data = await response.json();
        setSubscriptionUrl(data.subscriptionUrl);
        setHasExistingSubscription(true);
      } catch (err) {
        setError('Failed to create subscription URL. Please try again.');
      } finally {
        setIsCreatingSubscription(false);
      }
    },
    [listingId, isCreatingSubscription]
  );

  const handleRegenerateClick = useCallback(() => {
    setShowRegenerateConfirm(true);
  }, []);

  const handleConfirmRegenerate = useCallback(() => {
    handleCreateSubscription(true);
  }, [handleCreateSubscription]);

  const handleCancelRegenerate = useCallback(() => {
    setShowRegenerateConfirm(false);
  }, []);

  const handleCopyUrl = useCallback(() => {
    if (subscriptionUrl) {
      navigator.clipboard.writeText(subscriptionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [subscriptionUrl]);

  if (!listingId) {
    return null;
  }

  if (isLoading) {
    return (
      <div className={classes}>
        <div className={css.header}>
          <IconCalendar className={css.headerIcon} />
          <Heading size="2xs" fontWeight="bold" color="var(--text-primary)">
            <FormattedMessage id="CalendarExport.title" defaultMessage="Export Calendar" />
          </Heading>
        </div>
        <Text color="var(--text-primary)">
          <FormattedMessage id="CalendarExport.loading" defaultMessage="Loading..." />
        </Text>
      </div>
    );
  }

  return (
    <div className={classes}>
      <div className={css.header}>
        <IconCalendar className={css.headerIcon} />
        <Heading size="2xs" fontWeight="bold" color="var(--text-primary)">
          <FormattedMessage id="CalendarExport.title" defaultMessage="Export Calendar" />
        </Heading>
      </div>

      <Box mb="var(--spacing-6)">
        <Text color="var(--text-primary)">
          <FormattedMessage
            id="CalendarExport.description"
            defaultMessage="Sync your availability with Google Calendar, Apple Calendar, Outlook, or other calendar apps."
          />
        </Text>
      </Box>

      {/* Subscription Section */}
      <div className={css.section}>
        <Text fontWeight="bold" color="var(--text-primary)">
          <FormattedMessage
            id="CalendarExport.subscribeTitle"
            defaultMessage="Calendar Subscription"
          />
          {hasExistingSubscription && (
            <span className={css.activeIndicator}>
              <FormattedMessage id="CalendarExport.active" defaultMessage="Active" />
            </span>
          )}
        </Text>
        <Box mb="var(--spacing-4)">
          <Text color="var(--text-primary)">
            <FormattedMessage
              id="CalendarExport.subscribeDescription"
              defaultMessage="Get a URL that calendar apps can use to automatically sync updates."
            />
          </Text>
        </Box>

        {error && <p className={css.error}>{error}</p>}

        {!subscriptionUrl ? (
          <button
            type="button"
            className={css.exportButton}
            onClick={() => handleCreateSubscription(false)}
            disabled={isCreatingSubscription}
          >
            {isCreatingSubscription ? (
              <FormattedMessage id="CalendarExport.creating" defaultMessage="Creating..." />
            ) : (
              <FormattedMessage
                id="CalendarExport.getSubscriptionUrl"
                defaultMessage="Get Subscription URL"
              />
            )}
          </button>
        ) : (
          <div className={css.subscriptionUrlContainer}>
            <input
              type="text"
              className={css.subscriptionUrlInput}
              value={subscriptionUrl}
              readOnly
              onClick={e => e.target.select()}
            />
            <div className={css.subscriptionActions}>
              <button type="button" className={css.copyButton} onClick={handleCopyUrl}>
                {copied ? (
                  <FormattedMessage id="CalendarExport.copied" defaultMessage="Copied!" />
                ) : (
                  <FormattedMessage id="CalendarExport.copy" defaultMessage="Copy" />
                )}
              </button>
              <button
                type="button"
                className={css.regenerateButton}
                onClick={handleRegenerateClick}
                disabled={isCreatingSubscription}
              >
                <FormattedMessage id="CalendarExport.regenerate" defaultMessage="Regenerate" />
              </button>
            </div>

            {/* Regenerate Confirmation Dialog */}
            {showRegenerateConfirm && (
              <div className={css.confirmDialog}>
                <p className={css.confirmMessage}>
                  <FormattedMessage
                    id="CalendarExport.regenerateWarning"
                    defaultMessage="Regenerating will invalidate your current subscription URL. Any calendar apps using the old URL will no longer sync. Are you sure?"
                  />
                </p>
                <div className={css.confirmActions}>
                  <button
                    type="button"
                    className={css.confirmButton}
                    onClick={handleConfirmRegenerate}
                    disabled={isCreatingSubscription}
                  >
                    {isCreatingSubscription ? (
                      <FormattedMessage
                        id="CalendarExport.regenerating"
                        defaultMessage="Regenerating..."
                      />
                    ) : (
                      <FormattedMessage
                        id="CalendarExport.confirmRegenerate"
                        defaultMessage="Yes, Regenerate"
                      />
                    )}
                  </button>
                  <button
                    type="button"
                    className={css.cancelButton}
                    onClick={handleCancelRegenerate}
                  >
                    <FormattedMessage id="CalendarExport.cancel" defaultMessage="Cancel" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <button type="button" className={css.infoToggle} onClick={() => setShowInfo(!showInfo)}>
        <FormattedMessage
          id="CalendarExport.howToUse"
          defaultMessage="How to add to your calendar"
        />
      </button>

      {showInfo && (
        <div className={css.infoPanel}>
          <h4 className={css.infoTitle}>
            <FormattedMessage
              id="CalendarExport.subscriptionInstructions"
              defaultMessage="Using a Subscription URL (Recommended)"
            />
          </h4>
          <p className={css.infoDescription}>
            <FormattedMessage
              id="CalendarExport.subscriptionBenefit"
              defaultMessage="Your calendar will automatically update when bookings change."
            />
          </p>

          <h5 className={css.infoSubtitle}>
            <FormattedMessage id="CalendarExport.googleCalendar" defaultMessage="Google Calendar" />
          </h5>
          <ol className={css.infoList}>
            <li>
              <FormattedMessage
                id="CalendarExport.googleSub1"
                defaultMessage="Click 'Get Subscription URL' above and copy the URL"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.googleSub2"
                defaultMessage="Open Google Calendar and click the + next to 'Other calendars'"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.googleSub3"
                defaultMessage="Select 'From URL' and paste the subscription URL"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.googleSub4"
                defaultMessage="Click 'Add calendar' - it will sync automatically"
              />
            </li>
          </ol>

          <h5 className={css.infoSubtitle}>
            <FormattedMessage id="CalendarExport.appleCalendar" defaultMessage="Apple Calendar" />
          </h5>
          <ol className={css.infoList}>
            <li>
              <FormattedMessage
                id="CalendarExport.appleSub1"
                defaultMessage="Click 'Get Subscription URL' above and copy the URL"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.appleSub2"
                defaultMessage="In Calendar, go to File → New Calendar Subscription"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.appleSub3"
                defaultMessage="Paste the URL and click Subscribe"
              />
            </li>
          </ol>

          <h5 className={css.infoSubtitle}>
            <FormattedMessage id="CalendarExport.outlook" defaultMessage="Outlook" />
          </h5>
          <ol className={css.infoList}>
            <li>
              <FormattedMessage
                id="CalendarExport.outlookSub1"
                defaultMessage="Click 'Get Subscription URL' above and copy the URL"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.outlookSub2"
                defaultMessage="In Outlook, go to Add Calendar → Subscribe from web"
              />
            </li>
            <li>
              <FormattedMessage
                id="CalendarExport.outlookSub3"
                defaultMessage="Paste the URL and click Import"
              />
            </li>
          </ol>

          <div className={css.infoNote}>
            <strong>
              <FormattedMessage id="CalendarExport.securityNote" defaultMessage="Security Note:" />
            </strong>{' '}
            <FormattedMessage
              id="CalendarExport.securityDescription"
              defaultMessage="Keep your subscription URL private. Anyone with this URL can view your calendar. If you need to revoke access, click 'Regenerate' to create a new URL."
            />
          </div>
        </div>
      )}
    </div>
  );
};

CalendarExport.defaultProps = {
  className: null,
  rootClassName: null,
};

CalendarExport.propTypes = {
  className: string,
  rootClassName: string,
};

export default CalendarExport;
